import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../firebase/config";
import { collection, query, where, onSnapshot, addDoc, updateDoc, doc, orderBy, Timestamp } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad";

export default function Arqueo() {
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [movimientosPendientes, setMovimientosPendientes] = useState([]);
  const [arqueos, setArqueos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [observaciones, setObservaciones] = useState('');
  const [expandido, setExpandido] = useState(null);

  const esRecepcion = userRole === 'recepcion';
  const esAdmin = ['gestion_general', 'administracion'].includes(userRole);

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };

  function formatMonto(monto, moneda) {
    if (moneda === 'USD') return `USD ${parseFloat(monto).toFixed(2)}`;
    return `$${parseFloat(monto).toLocaleString('es-AR', { minimumFractionDigits: 2 })}`;
  }

  function totalPor(lista, moneda, formaPago) {
    return lista.filter(m => m.moneda === moneda && m.formaPago === formaPago).reduce((a, m) => a + m.monto, 0);
  }

  useEffect(() => {
    if (!clinicaId) return;
    if (esRecepcion) {
      const qPendientes = query(collection(db, 'caja'), where('clinicaId', '==', clinicaId), where('estado', '==', 'pendiente_arqueo'), where('registradoPor', '==', currentUser?.email), orderBy('fecha', 'asc'));
      const unsubPendientes = onSnapshot(qPendientes, snap => {
        setMovimientosPendientes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      });
      const qArqueos = query(collection(db, 'arqueos'), where('clinicaId', '==', clinicaId), where('realizadoPor', '==', currentUser?.email), orderBy('fechaArqueo', 'desc'));
      const unsubArqueos = onSnapshot(qArqueos, snap => setArqueos(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
      return () => { unsubPendientes(); unsubArqueos(); };
    } else {
      const qArqueos = query(collection(db, 'arqueos'), where('clinicaId', '==', clinicaId), orderBy('fechaArqueo', 'desc'));
      const unsubArqueos = onSnapshot(qArqueos, snap => {
        setArqueos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setLoading(false);
      });
      return () => { unsubArqueos(); };
    }
  }, [clinicaId, esRecepcion, currentUser]);

  async function confirmarArqueo(arqueoId, movimientosIds) {
    await updateDoc(doc(db, 'arqueos', arqueoId), { estado: 'confirmado', fechaConfirmacion: new Date(), confirmadoPor: currentUser?.email });
    for (const id of movimientosIds || []) {
      await updateDoc(doc(db, 'caja', id), { estado: 'arqueado' });
    }
    await registrarAccion(currentUser?.email, userRole, 'Arqueo', 'Confirmar arqueo', `Arqueo ID: ${arqueoId}`, clinicaId);
  }

  async function realizarArqueo() {
    const ingresos = movimientosPendientes.filter(m => m.tipo === 'ingreso');
    const egresos = movimientosPendientes.filter(m => m.tipo === 'egreso');
    const arqueo = {
      realizadoPor: currentUser?.email,
      fechaArqueo: new Date(),
      estado: 'pendiente_confirmacion',
      observaciones,
      movimientosIds: movimientosPendientes.map(m => m.id),
      clinicaId,
      resumen: {
        ingresosARSEfectivo: totalPor(ingresos, 'ARS', 'Efectivo'),
        ingresosARSTransferencia: totalPor(ingresos, 'ARS', 'Transferencia'),
        ingresosUSDEfectivo: totalPor(ingresos, 'USD', 'Efectivo'),
        ingresosUSDTransferencia: totalPor(ingresos, 'USD', 'Transferencia'),
        egresosARSEfectivo: totalPor(egresos, 'ARS', 'Efectivo'),
        egresosARSTransferencia: totalPor(egresos, 'ARS', 'Transferencia'),
        egresosUSDEfectivo: totalPor(egresos, 'USD', 'Efectivo'),
        egresosUSDTransferencia: totalPor(egresos, 'USD', 'Transferencia'),
      }
    };
    await addDoc(collection(db, 'arqueos'), arqueo);
    await registrarAccion(currentUser?.email, userRole, 'Arqueo', 'Realizar arqueo', `${movimientosPendientes.length} movimientos`, clinicaId);
    setObservaciones('');
  }

  const ingresos = movimientosPendientes.filter(m => m.tipo === 'ingreso');
  const egresos = movimientosPendientes.filter(m => m.tipo === 'egreso');

  const estadoArqueo = {
    pendiente_confirmacion: { label: 'Pendiente', color: theme.warning, bg: theme.warningBg },
    confirmado: { label: 'Confirmado', color: theme.success, bg: theme.successBg },
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Arqueo de turno" backLabel="Caja" backRuta="/caja" />
      <div style={{ padding: '24px', background: theme.bg }}>

        {esRecepcion && (
          <div style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '12px', fontWeight: '700', color: theme.secondary, marginBottom: '12px', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
              Movimientos pendientes de arqueo ({movimientosPendientes.length})
            </div>
            {loading
              ? <div style={{ color: theme.secondary, fontSize: '13px' }}>Cargando...</div>
              : movimientosPendientes.length === 0
                ? <div style={{ color: theme.secondary, fontSize: '13px', padding: '24px', textAlign: 'center', background: theme.bgCard, borderRadius: '10px', border: `0.5px solid ${theme.border}` }}>No hay movimientos pendientes</div>
                : <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                      {movimientosPendientes.map(m => (
                        <div key={m.id} style={{ background: theme.bgCard, borderRadius: '8px', padding: '10px 14px', border: `0.5px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontSize: '13px', color: theme.primary, fontWeight: '500' }}>{m.categoria} {m.detalle ? `· ${m.detalle}` : ''}</div>
                            <div style={{ fontSize: '11px', color: theme.secondary }}>{m.formaPago} · {m.paciente || '—'}</div>
                          </div>
                          <div style={{ fontSize: '15px', fontWeight: '700', color: m.tipo === 'ingreso' ? theme.success : theme.error }}>
                            {m.tipo === 'egreso' ? '−' : '+'}{formatMonto(m.monto, m.moneda)}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div style={{ background: theme.bgCard, borderRadius: '12px', padding: '16px', border: `0.5px solid ${theme.border}`, marginBottom: '16px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px', marginBottom: '12px' }}>
                        {[
                          { label: 'Ingresos ARS Efectivo', valor: totalPor(ingresos, 'ARS', 'Efectivo'), usd: false },
                          { label: 'Ingresos ARS Transf.', valor: totalPor(ingresos, 'ARS', 'Transferencia'), usd: false },
                          { label: 'Ingresos USD Efectivo', valor: totalPor(ingresos, 'USD', 'Efectivo'), usd: true },
                          { label: 'Ingresos USD Transf.', valor: totalPor(ingresos, 'USD', 'Transferencia'), usd: true },
                        ].map(item => (
                          <div key={item.label} style={{ background: theme.successBg, padding: '10px 12px', borderRadius: '8px' }}>
                            <div style={{ fontSize: '10px', color: theme.success, marginBottom: '4px', fontWeight: '600' }}>{item.label}</div>
                            <div style={{ fontSize: '15px', fontWeight: '700', color: theme.success }}>{item.usd ? 'USD ' : '$'}{item.valor.toLocaleString('es-AR', { minimumFractionDigits: 2 })}</div>
                          </div>
                        ))}
                      </div>
                      <div>
                        <label style={labelStyle}>OBSERVACIONES</label>
                        <textarea value={observaciones} onChange={e => setObservaciones(e.target.value)} rows={2} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Notas del turno..." />
                      </div>
                    </div>

                    <button onClick={realizarArqueo} style={{ ...btnPrimaryStyle, width: '100%', padding: '12px' }}>
                      Confirmar arqueo de turno
                    </button>
                  </>
            }
          </div>
        )}

        <div>
          <div style={{ fontSize: '12px', fontWeight: '700', color: theme.secondary, marginBottom: '12px', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
            Historial de arqueos
          </div>
          {arqueos.length === 0
            ? <div style={{ color: theme.secondary, fontSize: '13px', padding: '24px', textAlign: 'center', background: theme.bgCard, borderRadius: '10px', border: `0.5px solid ${theme.border}` }}>No hay arqueos registrados</div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {arqueos.map(a => {
                  const estado = estadoArqueo[a.estado] || { label: a.estado, color: theme.secondary, bg: theme.bgCard };
                  const fecha = a.fechaArqueo?.toDate ? a.fechaArqueo.toDate().toLocaleString('es-AR') : '—';
                  return (
                    <div key={a.id} style={{ background: theme.bgCard, borderRadius: '10px', border: `0.5px solid ${theme.border}` }}>
                      <div onClick={() => setExpandido(expandido === a.id ? null : a.id)} style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: theme.primary }}>{fecha}</div>
                          <div style={{ fontSize: '11px', color: theme.secondary }}>{a.realizadoPor}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '99px', background: estado.bg, color: estado.color, fontWeight: '500' }}>{estado.label}</span>
                          {esAdmin && a.estado === 'pendiente_confirmacion' && (
                            <button onClick={e => { e.stopPropagation(); confirmarArqueo(a.id, a.movimientosIds); }} style={btnPrimaryStyle}>Confirmar</button>
                          )}
                          {userRole === 'gestion_general' && (
                            <button onClick={async e => { e.stopPropagation(); if (!window.confirm('¿Eliminar este arqueo?')) return; const { deleteDoc, doc: dDoc } = await import('firebase/firestore'); await dDoc(db, 'arqueos', a.id); }} style={{ ...btnSecondaryStyle, color: theme.error, borderColor: theme.error }}>Eliminar</button>
                          )}
                        </div>
                      </div>
                      {expandido === a.id && a.resumen && (
                        <div style={{ padding: '12px 16px', borderTop: `0.5px solid ${theme.border}`, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                          {Object.entries(a.resumen).map(([k, v]) => (
                            <div key={k} style={{ background: theme.bg, padding: '8px 10px', borderRadius: '6px' }}>
                              <div style={{ fontSize: '10px', color: theme.secondary, marginBottom: '2px' }}>{k}</div>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: theme.primary }}>{typeof v === 'number' ? v.toLocaleString('es-AR', { minimumFractionDigits: 2 }) : v}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
          }
        </div>
      </div>
    </div>
  );
}